import { useEffect, useState } from "react"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { MenuClient } from "../MenuClient"
import { Header } from "../../common/header/Header"
import { IMG } from "../../common/CONST"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { useNavigate } from "react-router-dom"
import { IReplenishmentListProjection } from "../../../types/replenishment/IReplenishmentListProjection"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { getMskTime, round2Digits, spacedNum } from "../../common/CommonHelper"
import { ReplenishmentStatus } from "../../../types/replenishment/ReplenishmentStatus"
import { IReplenishmentStatus } from "../../../types/replenishment/IReplenishmentStatus"
import { IPageResponse } from "../../../types/IPageResponse"
import { ICheckConversationResponse } from "../../../types/messenger/ICheckConversationResponse"
import { Messenger } from "../../common/messenger/Messenger"
import { CONFIG } from "../../../CONFIG"
import Button from "@/ui/components/Button"
import Skeleton from "@/ui/components/Skeleton"
import EmptyRow from "@/ui/components/EmptyRow"
import { CURRENCY_REPLENISHMENT_NEW_ROUTE, getCurrencyReplenishmentViewRoute } from "@/routes"
import Input from "@/ui/components/Input"

const getRowClazz = (status: string) => {
  switch (status) {
    case "NEW":
    case "DOC_ACC":
    case "CR_ACC":
    case "CR_AWT":
      return "global-item-active"
    case "DONE":
      return "global-item-done"
    default:
      return "global-item"
  }
}

const getStatusClazz = (status: string) => {
  switch (status) {
    case "DOC_NAC":
    case "CR_NAC":
      return "status-no"
    case "NEW":
    case "DOC_ACC":
    case "BILL":
    case "CR_AWT":
    case "CR_ACC":
      return "status-ok"
    default:
      return "status"
  }
}

export const ClientCurrencyReplenishmentList = () => {
  const navigate = useNavigate()

  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [replenishments, setReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [search, setSearch] = useState("")
  const [filteredReplenishments, setFilteredReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [status, setStatus] = useState("")
  const [isLoading, setLoading] = useState(true)
  const [isRefetchLoading, setRefetchLoading] = useState(false)

  /* MESSENGER FCN */
  const [instanceId, setInstanceId] = useState<string>()
  const [conversationUserId, setConversationUserId] = useState<number>()
  const CHAT_INSTANCE = "CURREPLENISHMENT"
  const [unreadList, setUnreadList] = useState<IPageResponse<ICheckConversationResponse>>()

  const fetchUnread = () => {
    TrinityAPI.getUnreadConversations(CHAT_INSTANCE, response => {
      if (response.status === 200) {
        setUnreadList(response.data)
      }
    })
  }

  const closeMessenger = () => {
    setInstanceId(undefined)
    setConversationUserId(undefined)
  }

  const showMessenger = (instanceId: string | undefined, userId: number | undefined) => {
    setInstanceId(instanceId)
    setConversationUserId(userId)
  }

  const getUnreadCounter = (instanceId: string | undefined): number => {
    let counter = 0
    if (!instanceId) return counter
    let unreadItem = unreadList?.items.filter(item => item.instanceId === instanceId)[0]
    counter = unreadItem ? unreadItem.num : 0
    return counter
  }

  const navigateToReplenishment = (id: number) => {
    window.open(getCurrencyReplenishmentViewRoute(id), "_blank")
  }

  /* EOF MESSENGER FCN */

  const fetchReplenishments = () => {
    setSearch("")

    TrinityAPI.getCurrencyReplenishmentList(
      status,
      response => {
        if (response.status === 200) {
          setReplenishments(response.data)
          setFilteredReplenishments(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка получения пополнений: ${response.data.message}`
          })
        }
      },
      () => {
        setLoading(false)
        setRefetchLoading(false)
      }
    )
  }

  const refetchData = () => {
    setRefetchLoading(true)
    fetchReplenishments()
  }

  useEffect(() => {
    fetchReplenishments()
  }, [status])

  useEffect(() => {
    if (!search) {
      setFilteredReplenishments(replenishments)
      return
    }

    setFilteredReplenishments(
      replenishments.filter(
        r =>
          (r.contractor && r.contractor.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
          (r.country && r.country.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
          (r.invoiceAmount && round2Digits(Number(r.invoiceAmount) / 100).toString() === search.toLocaleLowerCase()) ||
          (r.id && r.id.toString() === search) ||
          (r.currency && r.currency.toLocaleLowerCase() === search.toLocaleLowerCase())
      )
    )
  }, [search])

  useEffect(() => {
    fetchUnread()

    const intervalId = setInterval(() => {
      fetchUnread()
    }, CONFIG.fetchUnreadsInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <>
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
      <MenuClient active={"currency-replenishment"} />
      <main className="main-container" id="scrollable">
        <Header />
        <div className="container">
          <h1 className="ui-heading">Пополнения валютные</h1>

          <section className="header-chapter">
            <div className="headr-chapter-name">
              <div className="header-chapter-icon">
                <img src={IMG.deals} alt="" className="header-chapter-img" />
              </div>
              <Button variant="black" onClick={refetchData} loading={isRefetchLoading}>
                Обновить список пополнений
              </Button>
            </div>
            <div className="flex-row-10 flex-wrap">
              <Button
                onClick={() => navigate(CURRENCY_REPLENISHMENT_NEW_ROUTE)}
                leftIcon={<img src={IMG.plus_btn} alt="" className="img-btn" />}
              >
                Добавить
              </Button>
            </div>
          </section>

          <section className="search-block">
            <Input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по контрагенту, стране, сумме или валюте"
            />
          </section>

          <div className="main-content--wrapper">
            <section className="main-content">
              <ul className="header-global-list">
                <li className="header-global-item width-130">Дата и номер</li>
                <li className="header-global-item cols-deals">Страна</li>
                <li className="header-global-item cols-deals" style={{ paddingLeft: "10px" }}>
                  Плательщик
                </li>
                <li className="header-global-item width-130" style={{ paddingLeft: "10px" }}>
                  Сумма инвойса
                </li>
                <li className="header-global-item width-100">Валюта</li>
                <li className="header-global-item width-130" style={{ paddingLeft: "10px" }}>
                  Сумма к зачислению
                </li>
                <li className="header-global-item width-130">
                  <select value={status} className="select-style" onChange={e => setStatus(e.target.value)}>
                    <option value="" key="EMPTY">
                      Статус
                    </option>
                    <option value="ACTION_REQUIRED" key="ACTION_REQUIRED">
                      Требуется действие
                    </option>
                    {Object.keys(ReplenishmentStatus).map((key, index) =>
                      ReplenishmentStatus[key as keyof IReplenishmentStatus][1] ? (
                        <option value={key} key={index}>
                          {ReplenishmentStatus[key as keyof IReplenishmentStatus][1]}
                        </option>
                      ) : null
                    )}
                  </select>
                </li>
              </ul>
              <ul className="global-list">
                {isLoading && (
                  <div className="skeleton-wrapper">
                    <Skeleton rows={4} gutterTop />
                  </div>
                )}
                {!isLoading &&
                  (filteredReplenishments.length > 0 ? (
                    filteredReplenishments.map((r, index) => (
                      <li className={getRowClazz(r.status)} id="deals-info-open" key={index}>
                        <div className="global-scroll-block">
                          <p className="global-item-text width-130" onClick={() => navigateToReplenishment(r.id)}>
                            <span>№{r.id}</span>
                            <span dangerouslySetInnerHTML={{ __html: getMskTime(r.createdAt || "") || "" }} />
                          </p>
                          <p className="deal-item-flex cols-deals" onClick={() => navigateToReplenishment(r.id)}>
                            <span className="deal-name">{r.country || "-"}</span>
                          </p>
                          <p className="deal-item-flex cols-deals" onClick={() => navigateToReplenishment(r.id)}>
                            <span className="deal-name">{r.currencyPayer || "-"}</span>
                          </p>
                          <p className="global-item-text width-130" onClick={() => navigateToReplenishment(r.id)}>
                            {r.invoiceAmount ? spacedNum(round2Digits(Number(r.invoiceAmount) / 100)) : "-"}
                          </p>
                          <p
                            className="global-item-text globak-num width-100"
                            onClick={() => navigateToReplenishment(r.id)}
                          >
                            {r.currency || "-"}
                          </p>
                          <p className="global-item-text width-130" onClick={() => navigateToReplenishment(r.id)}>
                            {r.creditedAmount ? spacedNum(round2Digits(Number(r.creditedAmount) / 100)) : "-"}
                          </p>
                          <p
                            className={`${getStatusClazz(r.status)} width-130`}
                            onClick={() => navigateToReplenishment(r.id)}
                          >
                            {r.status ? ReplenishmentStatus[r.status as keyof IReplenishmentStatus][1] : ""}
                          </p>
                        </div>
                        <button className="chat-deals" onClick={() => showMessenger(r.id?.toString(), r.userId)}>
                          {getUnreadCounter(r.id?.toString()) > 0 ? (
                            <span className="message-new">{getUnreadCounter(r.id?.toString())}</span>
                          ) : (
                            ""
                          )}
                        </button>
                      </li>
                    ))
                  ) : (
                    <EmptyRow text="Пополнений не найдено" />
                  ))}
              </ul>
            </section>
          </div>
        </div>
        <Messenger instanceId={instanceId} type={CHAT_INSTANCE} userId={conversationUserId} close={closeMessenger} />
      </main>
    </>
  )
}
